import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
  Output, EventEmitter, model
} from '@angular/core';
import {KeyValue} from '@angular/common';
import {ImageAnnotation, ImageAnnotationElement, TupleOfintAndint} from '../../../../api/api-client';
import {PictureAnnotationWidgetService} from '../../../../services/picture-annotation-widget.service';

@Component({
  selector: 'app-canvas',
  imports: [],
  providers: [],
  templateUrl: './canvas.component.html',
  styleUrl: './canvas.component.scss'
})
export class CanvasComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('paintCanvas', {static: false}) canvas!: ElementRef<HTMLCanvasElement>;
  @Input() currentColor!: string;
  @Input() annotationData!: ImageAnnotation | undefined;
  @Input() base64ImageString!: string;
  @Input() drawingMode!: 'free' | 'circle';
  @Input() circleRadius!: number;
  @Input() lineWidth!: number;
  @Output() drawnWithColor=new EventEmitter<string>();
  @Output() redoneWithColor=new EventEmitter<string>();
  @Output() undoneWithColor=new EventEmitter<string>();

  private context!: CanvasRenderingContext2D | null;
  private isDrawing: boolean = false;
  private img!: HTMLImageElement;
  private canvasHistory: KeyValue<string,string>[] = [];

  private freeHandPointsCache:TupleOfintAndint[] = [];

  historyIndex = model<number>(0);
  canvasHistoryLength=model<number>(0);


  constructor(private pawService:PictureAnnotationWidgetService) {
  }


  // load a virtual image tag and feed it with the background image and resize the canvas accordingly
  // CAVE: resizing the canvas nullifies the canvas context, so call setupCanvas()
  ngOnInit(): void {
    this.img = new Image();
    this.img.loading = 'eager'
    this.img.src = this.base64ImageString
    this.img.onload = () => {
      if (this.context) {
        this.canvas.nativeElement.width = this.img.naturalWidth;
        this.canvas.nativeElement.height = this.img.naturalHeight;
        this.context.drawImage(this.img, 0, 0);
        this.setupCanvas();
        this.canvasHistory.push({value:this.canvas.nativeElement.toDataURL('image/png', 1),key:""});
        this.canvasHistoryLength.update((currentLength)=>this.canvasHistory.length);
        this.historyIndex.update((currentIndex)=>0);

        if(this.annotationData&&this.annotationData.annotationElements){
            for (const annotation of this.annotationData.annotationElements) {
              this.context.lineWidth = annotation.thicknessPx??1;
              this.context.strokeStyle = annotation.color??"black";
              switch (annotation.type) {
                case 'Circle':
                  this.context.beginPath();
                  this.context.globalCompositeOperation = 'source-over';
                  this.context.arc(annotation.circleCenter?.x??-1, annotation.circleCenter?.y??-1, this.circleRadius, 0, 2 * Math.PI);
                  this.context.stroke();
                  this.canvasHistory.push({value: this.canvas.nativeElement.toDataURL(), key: annotation.color??""});
                  this.canvasHistoryLength.update((currentLength)=>this.canvasHistory.length);
                  this.pawService.imageAnnotations.push({
                    circleCenter: {
                      x: annotation.circleCenter?.x??-1,
                      y: annotation.circleCenter?.y??-1
                    },
                    circleRadiusPx: annotation.circleRadiusPx,
                    type: "Circle",
                    color: annotation.color,
                    thicknessPx: annotation.thicknessPx,
                  } as ImageAnnotationElement)
                  this.historyIndex.update((currentIndex)=>currentIndex+1);
                  this.drawnWithColor.emit(annotation.color);
                  break;
                case 'Freehand':
                  if(annotation.freehandPoints){
                    this.context.beginPath();
                    this.context.globalCompositeOperation = 'source-over';

                    for(const point of annotation.freehandPoints){
                      this.context.moveTo(point?.x??-1, point?.y??-1);
                      this.context.lineTo(point?.x??-1, point?.y??-1);
                      this.context.stroke();
                    }
                    this.canvasHistory.push({value:this.canvas.nativeElement.toDataURL(),key:annotation.color??""});
                    this.canvasHistoryLength.update((currentLength)=>this.canvasHistory.length);
                    this.pawService.imageAnnotations.push({
                      type: "Freehand",
                      thicknessPx: annotation.thicknessPx,
                      freehandPoints: annotation.freehandPoints,
                      color: annotation.color??""
                    } as ImageAnnotationElement);
                    this.historyIndex.update((currentIndex)=>currentIndex+1);
                    this.drawnWithColor.emit(annotation.color??"");
                    break;
                  }
              }
            }
          }
        }
      }
    }

  ngAfterViewInit() {
    this.context = this.canvas.nativeElement.getContext('2d');
    this.setupCanvas();
    this.canvas.nativeElement.addEventListener('mousedown', (event) => this.startDrawing(event));
    this.canvas.nativeElement.addEventListener('mousemove', (event) => this.draw(event));
    this.canvas.nativeElement.addEventListener('mouseup', (event) => this.stopDrawing(event));
    this.canvas.nativeElement.addEventListener('mouseleave', (event) => this.stopDrawing(event));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.context) {
      this.setupCanvas()
    }
  }

  private setupCanvas() {
    if (this.context) {
      this.context.lineCap = 'round';
      this.context.lineWidth = this.lineWidth;
      this.context.strokeStyle = this.currentColor;
    }
  }

  private startDrawing(event: MouseEvent) {
    if (event.button == 0) {
      if (this.drawingMode === 'free') {
        this.isDrawing = true;
        this.freeHandPointsCache=[];
        this.draw(event); // To start drawing immediately from the initial point
      } else if (this.drawingMode === 'circle') {
        this.drawCircle(event);
      }
    }
  }

  private drawCircle(event: MouseEvent) {
    if (this.context) {
      let originalHistoryMaxIndex = this.canvasHistory.length-1;
      for(let i = this.historyIndex(); i< originalHistoryMaxIndex; i++) {
        this.canvasHistory.pop();
      }
      this.canvasHistoryLength.update((currentLength)=>this.canvasHistory.length);
      const rect = this.canvas.nativeElement.getBoundingClientRect();
      const scaleX = this.canvas.nativeElement.width / rect.width;
      const scaleY = this.canvas.nativeElement.height / rect.height;

      const centerX = (event.clientX - rect.left) * scaleX;
      const centerY = (event.clientY - rect.top) * scaleY;

      this.context.beginPath();
      this.context.globalCompositeOperation = 'source-over';
      this.context.arc(centerX, centerY, this.circleRadius, 0, 2 * Math.PI);
      this.context.stroke();
      this.canvasHistory.push({value:this.canvas.nativeElement.toDataURL(),key:this.currentColor});
      this.canvasHistoryLength.update((currentLength)=>this.canvasHistory.length);
      this.pawService.imageAnnotations.push({
        circleCenter: {
          x: Math.round(centerX),
          y: Math.round(centerY)
        },
        circleRadiusPx: this.circleRadius,
        type: "Circle",
        color: this.currentColor,
        thicknessPx: this.lineWidth
      }  as ImageAnnotationElement)
      this.historyIndex.update((currentIndex)=>currentIndex+1);
      event.stopPropagation();
      this.drawnWithColor.emit(this.currentColor);
    }

  }

  private draw(event: MouseEvent) {
    if (!this.isDrawing) return;
    if (event.button == 0) {
      if (this.context) {
        let originalHistoryMaxIndex = this.canvasHistory.length-1;
        for(let i = this.historyIndex(); i< originalHistoryMaxIndex; i++) {
          this.canvasHistory.pop();
        }
        this.canvasHistoryLength.update((currentLength)=>this.canvasHistory.length);
        this.context.beginPath();
        this.context.globalCompositeOperation = 'source-over';
        const rect = this.canvas.nativeElement.getBoundingClientRect();
        const scaleX = this.canvas.nativeElement.width / rect.width;
        const scaleY = this.canvas.nativeElement.height / rect.height;

        const x = (event.clientX - rect.left) * scaleX;
        const y = (event.clientY - rect.top) * scaleY;
        this.context.moveTo(x, y);
        this.context.lineTo(x, y);
        this.context.stroke();
        this.freeHandPointsCache.push({x:Math.round(x),y:Math.round(y)} as TupleOfintAndint)
        event.stopPropagation();
      }
    }
  }

  private stopDrawing(event: MouseEvent) {
    if (this.drawingMode === 'free') {
      if(this.isDrawing){
        this.canvasHistory.push({value:this.canvas.nativeElement.toDataURL(),key:this.currentColor});
        this.canvasHistoryLength.update((currentLength)=>this.canvasHistory.length);
        this.pawService.imageAnnotations.push({
          type: "Freehand",
          thicknessPx: this.lineWidth,
          freehandPoints: this.freeHandPointsCache,
          color: this.currentColor
        } as ImageAnnotationElement);
        this.freeHandPointsCache=[];
        this.historyIndex.update((currentIndex)=>currentIndex+1);
        this.drawnWithColor.emit(this.currentColor);
      }
      this.isDrawing = false;
    }
    event.stopPropagation();
  }

  undo() {
    if (this.historyIndex() > 0) {
      this.historyIndex.update((currentIndex)=>currentIndex-1);
      let tempImg = new Image()
      tempImg.src=this.canvasHistory[this.historyIndex()].value;
      tempImg.onload=()=>this.context?.drawImage(tempImg,0,0)
      this.pawService.undoAnnotation();
      this.undoneWithColor.emit(this.canvasHistory[this.historyIndex()+1].key);
    }
  }

  redo() {
    if(this.historyIndex() < this.canvasHistory.length - 1) {
      this.historyIndex.update((currentIndex)=>currentIndex+1);
      let tempImg = new Image()
      tempImg.src=this.canvasHistory[this.historyIndex()].value;
      tempImg.onload=()=>this.context?.drawImage(tempImg,0,0)
      this.pawService.redoAnnotation();
      this.redoneWithColor.emit(this.canvasHistory[this.historyIndex()].key);
    }
  }

  // estimateStyle(): string {
  //   if (this.base64ImageString && this.imgLoaded) {
  //     return `width: ${this.img.naturalWidth}px; height: ${this.img.naturalHeight}px;`;
  //   }
  //   return ""
  // }

}
